import { InductionRecord, Question, PostTestQuestion, AdminAccount, InstructorMap, MandatoryTrainingRecord, MandatorySettings } from '../types';
import { INITIAL_QUESTIONS, DEFAULT_POST_TEST_QUESTIONS, INSTRUCTORS, SAFETY_INDUCTION_PDF_URL } from '../constants';
import { MANDATORY_MODULES } from '../constantsMandatory';
import { db } from './firebase';
import { collection, addDoc, getDocs, doc, getDoc, setDoc, deleteDoc, onSnapshot, query, where, limit } from 'firebase/firestore';

const RECORDS_COLLECTION = 'induction_records';
const MANDATORY_TRAINING_COLLECTION = 'mandatory_training_records';
const SETTINGS_COLLECTION = 'app_settings';
const QUESTIONS_DOC = 'questions_list';
const POST_TEST_QUESTIONS_DOC = 'post_test_questions_list';
const INSTRUCTORS_CONFIG_DOC = 'instructors_map_config';
const MANDATORY_SETTINGS_DOC = 'mandatory_settings_config';
const MANDATORY_QUESTIONS_COLLECTION = 'mandatory_questions';

// Cache TTL Configurations for Firebase Quota Optimization
const CONFIG_CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours for static configs (questions, instructors, settings)
const RECORDS_CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes for full dashboard records
const TARGETED_QUERY_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes for per-user NIK or Name queries

interface CacheEnvelope<T> {
  data: T;
  timestamp: number;
}

function getFromCacheWithTtl<T>(key: string, ttlMs: number): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const envelope: CacheEnvelope<T> = JSON.parse(raw);
    if (Date.now() - envelope.timestamp < ttlMs) {
      return envelope.data;
    }
  } catch (e) {
    // fallback
  }
  return null;
}

function setToCacheWithTtl<T>(key: string, data: T): void {
  try {
    const envelope: CacheEnvelope<T> = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(envelope));
  } catch (e) {
    // fallback
  }
}

/**
 * Recursively strips undefined fields from an object so Firestore setDoc / addDoc never throws:
 * "Unsupported field value: undefined"
 */
export function removeUndefinedFields<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  try {
    return JSON.parse(JSON.stringify(obj, (_key, value) => {
      if (value === undefined) {
        return null;
      }
      return value;
    }));
  } catch (err) {
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
      return (obj as any[])
        .filter(item => item !== undefined)
        .map(item => removeUndefinedFields(item)) as unknown as T;
    }
    const result: any = {};
    for (const key of Object.keys(obj as any)) {
      const val = (obj as any)[key];
      if (val !== undefined) {
        result[key] = removeUndefinedFields(val);
      }
    }
    return result;
  }
}

// In-memory runtime caching to avoid re-fetching on rapid component re-renders
let inMemoryInductionRecords: { data: InductionRecord[]; timestamp: number } | null = null;
let inMemoryMTRecords: { data: MandatoryTrainingRecord[]; timestamp: number } | null = null;
let inMemoryAdmins: { data: AdminAccount[]; timestamp: number } | null = null;

export const getDefaultModuleQuestions = (): Record<string, PostTestQuestion[]> => {
  const result: Record<string, PostTestQuestion[]> = {};
  MANDATORY_MODULES.forEach(mod => {
    result[mod.id] = [...mod.questions];
  });
  return result;
};

export const DEFAULT_MANDATORY_SETTINGS: MandatorySettings = {
  passScore: 70,
  ehsManagerName: 'EHS Department Head',
  instructorName: 'Tim EHS Training PT United Tractors Tbk',
  allowedWorkAreas: ['Yard Sukapura', 'Yard Cakung', 'Yard Jababeka', 'Used'],
  certificateValidityYears: 1,
  activeModules: {
    'jsa': true,
    'defensive_driving': true,
    'loto': true,
    'slinging_lifting': true,
    'safety_golden_rules': true,
    'hand_finger_injury': true,
    'power_tools': true
  },
  modulePdfs: {
    'jsa': SAFETY_INDUCTION_PDF_URL,
    'defensive_driving': SAFETY_INDUCTION_PDF_URL,
    'loto': SAFETY_INDUCTION_PDF_URL,
    'slinging_lifting': SAFETY_INDUCTION_PDF_URL,
    'safety_golden_rules': SAFETY_INDUCTION_PDF_URL,
    'hand_finger_injury': SAFETY_INDUCTION_PDF_URL,
    'power_tools': SAFETY_INDUCTION_PDF_URL
  },
  customQuestions: getDefaultModuleQuestions(),
  safetyInductionPdfUrl: SAFETY_INDUCTION_PDF_URL
};

/**
 * Fetch all induction records (primarily for Admin Dashboard).
 * Uses in-memory and sessionStorage cache with 3-minute TTL to prevent exhausting Firebase quota.
 */
export const getRecords = async (forceRefresh = false): Promise<InductionRecord[]> => {
  // Check memory cache
  if (!forceRefresh && inMemoryInductionRecords && (Date.now() - inMemoryInductionRecords.timestamp < RECORDS_CACHE_TTL_MS)) {
    return inMemoryInductionRecords.data;
  }

  // Check sessionStorage cache
  if (!forceRefresh) {
    try {
      const stored = sessionStorage.getItem('ut_cache_induction_records');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Date.now() - parsed.timestamp < RECORDS_CACHE_TTL_MS) {
          inMemoryInductionRecords = parsed;
          return parsed.data;
        }
      }
    } catch (e) {}
  }

  try {
    const querySnapshot = await getDocs(collection(db, RECORDS_COLLECTION));
    const records: InductionRecord[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as InductionRecord;
      records.push({
        ...data,
        id: data.id || doc.id
      });
    });
    // Sort by timestamp descending
    const sorted = records.sort((a, b) => b.timestamp - a.timestamp);
    inMemoryInductionRecords = { data: sorted, timestamp: Date.now() };
    try {
      sessionStorage.setItem('ut_cache_induction_records', JSON.stringify(inMemoryInductionRecords));
    } catch (e) {}
    return sorted;
  } catch (error) {
    console.error("Error fetching records: ", error);
    if (inMemoryInductionRecords) return inMemoryInductionRecords.data;
    return [];
  }
};

/**
 * Check if participant already has an active induction certificate.
 * Performs a TARGETED Firestore query (limit 5) instead of reading thousands of records,
 * saving ~99.8% Firebase reads when 150 people access the app.
 */
export const getActiveInductionByName = async (fullName: string): Promise<InductionRecord | null> => {
  if (!fullName || !fullName.trim()) return null;
  const cleanName = fullName.trim();

  // If in-memory records are loaded (e.g. admin session), search locally without Firestore read
  if (inMemoryInductionRecords) {
    const match = inMemoryInductionRecords.data.find(r => {
      if (r.fullName && r.fullName.trim().toLowerCase() === cleanName.toLowerCase()) {
        const indDate = new Date(r.inductionDate);
        const expDate = new Date(indDate);
        expDate.setFullYear(expDate.getFullYear() + 1);
        return expDate.getTime() > Date.now() && r.postTestPassed !== false;
      }
      return false;
    });
    if (match) return match;
  }

  // Check session cache for this specific name
  const cacheKey = `ut_cache_active_ind_${cleanName.toLowerCase()}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < TARGETED_QUERY_CACHE_TTL_MS) {
        return parsed.data;
      }
    }
  } catch (e) {}

  try {
    // Targeted query for only the participant's name
    const q = query(
      collection(db, RECORDS_COLLECTION),
      where("fullName", "==", cleanName),
      limit(5)
    );
    const snap = await getDocs(q);
    let matched: InductionRecord | null = null;
    snap.forEach((doc) => {
      const data = doc.data() as InductionRecord;
      const rec = { ...data, id: data.id || doc.id };
      const indDate = new Date(rec.inductionDate);
      const expDate = new Date(indDate);
      expDate.setFullYear(expDate.getFullYear() + 1);
      if (expDate.getTime() > Date.now() && rec.postTestPassed !== false) {
        matched = rec;
      }
    });

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ data: matched, timestamp: Date.now() }));
    } catch (e) {}

    return matched;
  } catch (error) {
    console.error("Error checking active induction: ", error);
    return null;
  }
};

export const saveRecord = async (record: InductionRecord): Promise<void> => {
  try {
    const payload = removeUndefinedFields(record);
    await setDoc(doc(db, RECORDS_COLLECTION, record.id), payload);
    
    // Update local cache immediately
    if (inMemoryInductionRecords) {
      inMemoryInductionRecords.data = [record, ...inMemoryInductionRecords.data.filter(r => r.id !== record.id)];
      inMemoryInductionRecords.timestamp = Date.now();
      try {
        sessionStorage.setItem('ut_cache_induction_records', JSON.stringify(inMemoryInductionRecords));
      } catch (e) {}
    }

    // Invalidate/update session cache for this user
    if (record.fullName) {
      const cacheKey = `ut_cache_active_ind_${record.fullName.trim().toLowerCase()}`;
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ data: record, timestamp: Date.now() }));
      } catch (e) {}
    }
  } catch (error) {
    console.error("Error saving record: ", error);
    throw error;
  }
};

export const deleteRecord = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, RECORDS_COLLECTION, id));
    if (inMemoryInductionRecords) {
      inMemoryInductionRecords.data = inMemoryInductionRecords.data.filter(r => r.id !== id);
      try {
        sessionStorage.setItem('ut_cache_induction_records', JSON.stringify(inMemoryInductionRecords));
      } catch (e) {}
    }
  } catch (error) {
    console.error("Error deleting record: ", error);
    throw error;
  }
};

/**
 * Fetch Questions with 2-hour TTL cache.
 * Trainees load this instantly from localStorage without reading from Firestore.
 */
export const getQuestions = async (forceRefresh = false): Promise<Question[]> => {
  if (!forceRefresh) {
    const cached = getFromCacheWithTtl<Question[]>('ut_questions_cache_v4', CONFIG_CACHE_TTL_MS);
    if (cached && cached.length > 0) {
      return cached;
    }
  }

  try {
    const docRef = doc(db, SETTINGS_COLLECTION, QUESTIONS_DOC);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const list = (data.questions as Question[]) || INITIAL_QUESTIONS;
      const finalList = list.length > 0 ? list : INITIAL_QUESTIONS;
      setToCacheWithTtl('ut_questions_cache_v4', finalList);
      return finalList;
    } else {
      await setDoc(docRef, { questions: INITIAL_QUESTIONS });
      setToCacheWithTtl('ut_questions_cache_v4', INITIAL_QUESTIONS);
      return INITIAL_QUESTIONS;
    }
  } catch (error) {
    console.error("Error fetching questions: ", error);
    const cached = getFromCacheWithTtl<Question[]>('ut_questions_cache_v4', Infinity);
    return cached && cached.length > 0 ? cached : INITIAL_QUESTIONS;
  }
};

export const subscribeQuestions = (callback: (questions: Question[]) => void): (() => void) => {
  const docRef = doc(db, SETTINGS_COLLECTION, QUESTIONS_DOC);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      const list = (data.questions as Question[]) || INITIAL_QUESTIONS;
      const finalList = list.length > 0 ? list : INITIAL_QUESTIONS;
      setToCacheWithTtl('ut_questions_cache_v4', finalList);
      callback(finalList);
    } else {
      setDoc(docRef, { questions: INITIAL_QUESTIONS }).then(() => {
        setToCacheWithTtl('ut_questions_cache_v4', INITIAL_QUESTIONS);
        callback(INITIAL_QUESTIONS);
      }).catch(err => {
        console.error("Error initializing questions inside subscriber: ", err);
        callback(INITIAL_QUESTIONS);
      });
    }
  }, (error) => {
    console.error("Error subscribing to questions: ", error);
    const cached = getFromCacheWithTtl<Question[]>('ut_questions_cache_v4', Infinity);
    callback(cached && cached.length > 0 ? cached : INITIAL_QUESTIONS);
  });
};

export const saveQuestions = async (questions: Question[]): Promise<void> => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, QUESTIONS_DOC);
    const payload = removeUndefinedFields({ questions });
    await setDoc(docRef, payload);
    setToCacheWithTtl('ut_questions_cache_v4', questions);
  } catch (error) {
    console.error("Error saving questions: ", error);
    throw error;
  }
};

/**
 * Fetch Post-Test Questions with 2-hour TTL cache.
 */
export const getPostTestQuestions = async (forceRefresh = false): Promise<PostTestQuestion[]> => {
  if (!forceRefresh) {
    const cached = getFromCacheWithTtl<PostTestQuestion[]>('ut_pt_questions_cache_v4', CONFIG_CACHE_TTL_MS);
    if (cached && cached.length > 0) {
      return cached;
    }
  }

  try {
    const docRef = doc(db, SETTINGS_COLLECTION, POST_TEST_QUESTIONS_DOC);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const list = (data.questions as PostTestQuestion[]) || DEFAULT_POST_TEST_QUESTIONS;
      const finalList = list.length > 0 ? list : DEFAULT_POST_TEST_QUESTIONS;
      setToCacheWithTtl('ut_pt_questions_cache_v4', finalList);
      return finalList;
    } else {
      await setDoc(docRef, { questions: DEFAULT_POST_TEST_QUESTIONS });
      setToCacheWithTtl('ut_pt_questions_cache_v4', DEFAULT_POST_TEST_QUESTIONS);
      return DEFAULT_POST_TEST_QUESTIONS;
    }
  } catch (error) {
    console.error("Error fetching post-test questions: ", error);
    const cached = getFromCacheWithTtl<PostTestQuestion[]>('ut_pt_questions_cache_v4', Infinity);
    return cached && cached.length > 0 ? cached : DEFAULT_POST_TEST_QUESTIONS;
  }
};

export const subscribePostTestQuestions = (callback: (questions: PostTestQuestion[]) => void): (() => void) => {
  const docRef = doc(db, SETTINGS_COLLECTION, POST_TEST_QUESTIONS_DOC);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      const list = (data.questions as PostTestQuestion[]) || DEFAULT_POST_TEST_QUESTIONS;
      const finalList = list.length > 0 ? list : DEFAULT_POST_TEST_QUESTIONS;
      setToCacheWithTtl('ut_pt_questions_cache_v4', finalList);
      callback(finalList);
    } else {
      setDoc(docRef, { questions: DEFAULT_POST_TEST_QUESTIONS }).then(() => {
        setToCacheWithTtl('ut_pt_questions_cache_v4', DEFAULT_POST_TEST_QUESTIONS);
        callback(DEFAULT_POST_TEST_QUESTIONS);
      }).catch(err => {
        console.error("Error initializing post-test questions in subscriber: ", err);
        callback(DEFAULT_POST_TEST_QUESTIONS);
      });
    }
  }, (error) => {
    console.error("Error subscribing to post-test questions: ", error);
    const cached = getFromCacheWithTtl<PostTestQuestion[]>('ut_pt_questions_cache_v4', Infinity);
    callback(cached && cached.length > 0 ? cached : DEFAULT_POST_TEST_QUESTIONS);
  });
};

export const savePostTestQuestions = async (questions: PostTestQuestion[]): Promise<void> => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, POST_TEST_QUESTIONS_DOC);
    const payload = removeUndefinedFields({ questions });
    await setDoc(docRef, payload);
    setToCacheWithTtl('ut_pt_questions_cache_v4', questions);
  } catch (error) {
    console.error("Error saving post-test questions: ", error);
    throw error;
  }
};

const ADMINS_COLLECTION = 'admins';

export const getAdmins = async (forceRefresh = false): Promise<AdminAccount[]> => {
  if (!forceRefresh && inMemoryAdmins && (Date.now() - inMemoryAdmins.timestamp < RECORDS_CACHE_TTL_MS)) {
    return inMemoryAdmins.data;
  }

  try {
    const querySnapshot = await getDocs(collection(db, ADMINS_COLLECTION));
    const admins: AdminAccount[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as AdminAccount;
      admins.push({
        ...data,
        id: data.id || doc.id
      });
    });
    const sorted = admins.sort((a, b) => b.createdAt - a.createdAt);
    inMemoryAdmins = { data: sorted, timestamp: Date.now() };
    return sorted;
  } catch (error) {
    console.error("Error fetching admins: ", error);
    if (inMemoryAdmins) return inMemoryAdmins.data;
    return [];
  }
};

export const saveAdmin = async (admin: AdminAccount): Promise<void> => {
  try {
    const payload = removeUndefinedFields(admin);
    await setDoc(doc(db, ADMINS_COLLECTION, admin.id), payload);
    if (inMemoryAdmins) {
      inMemoryAdmins.data = [admin, ...inMemoryAdmins.data.filter(a => a.id !== admin.id)];
    }
  } catch (error) {
    console.error("Error saving admin: ", error);
    throw error;
  }
};

export const getInstructorMap = async (forceRefresh = false): Promise<InstructorMap> => {
  if (!forceRefresh) {
    const cached = getFromCacheWithTtl<InstructorMap>('ut_instructors_cache_v4', CONFIG_CACHE_TTL_MS);
    if (cached && Object.keys(cached).length > 0) {
      return cached;
    }
  }

  try {
    const docRef = doc(db, SETTINGS_COLLECTION, INSTRUCTORS_CONFIG_DOC);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const map = (data.instructorMap as InstructorMap) || INSTRUCTORS;
      setToCacheWithTtl('ut_instructors_cache_v4', map);
      return map;
    } else {
      await setDoc(docRef, { instructorMap: INSTRUCTORS });
      setToCacheWithTtl('ut_instructors_cache_v4', INSTRUCTORS);
      return INSTRUCTORS;
    }
  } catch (error) {
    console.error("Error fetching instructor map: ", error);
    const cached = getFromCacheWithTtl<InstructorMap>('ut_instructors_cache_v4', Infinity);
    return cached || INSTRUCTORS;
  }
};

export const subscribeInstructorMap = (callback: (instructorMap: InstructorMap) => void): (() => void) => {
  const docRef = doc(db, SETTINGS_COLLECTION, INSTRUCTORS_CONFIG_DOC);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      const map = (data.instructorMap as InstructorMap) || INSTRUCTORS;
      setToCacheWithTtl('ut_instructors_cache_v4', map);
      callback(map);
    } else {
      setDoc(docRef, { instructorMap: INSTRUCTORS }).then(() => {
        setToCacheWithTtl('ut_instructors_cache_v4', INSTRUCTORS);
        callback(INSTRUCTORS);
      }).catch(err => {
        console.error("Error initializing instructor map inside subscriber: ", err);
        callback(INSTRUCTORS);
      });
    }
  }, (error) => {
    console.error("Error subscribing to instructor map: ", error);
    const cached = getFromCacheWithTtl<InstructorMap>('ut_instructors_cache_v4', Infinity);
    callback(cached || INSTRUCTORS);
  });
};

export const saveInstructorMap = async (instructorMap: InstructorMap): Promise<void> => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, INSTRUCTORS_CONFIG_DOC);
    const payload = removeUndefinedFields({ instructorMap });
    await setDoc(docRef, payload);
    setToCacheWithTtl('ut_instructors_cache_v4', instructorMap);
  } catch (error) {
    console.error("Error saving instructor map: ", error);
    throw error;
  }
};

/**
 * Fetch all mandatory training records (for Admin Dashboard).
 * Uses in-memory and sessionStorage cache with 3-minute TTL to save quota.
 */
export const getMandatoryTrainingRecords = async (forceRefresh = false): Promise<MandatoryTrainingRecord[]> => {
  if (!forceRefresh && inMemoryMTRecords && (Date.now() - inMemoryMTRecords.timestamp < RECORDS_CACHE_TTL_MS)) {
    return inMemoryMTRecords.data;
  }

  if (!forceRefresh) {
    try {
      const stored = sessionStorage.getItem('ut_cache_mt_records');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Date.now() - parsed.timestamp < RECORDS_CACHE_TTL_MS) {
          inMemoryMTRecords = parsed;
          return parsed.data;
        }
      }
    } catch (e) {}
  }

  try {
    const querySnapshot = await getDocs(collection(db, MANDATORY_TRAINING_COLLECTION));
    const records: MandatoryTrainingRecord[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as MandatoryTrainingRecord;
      records.push({
        ...data,
        id: data.id || doc.id
      });
    });
    const sorted = records.sort((a, b) => b.timestamp - a.timestamp);
    inMemoryMTRecords = { data: sorted, timestamp: Date.now() };
    try {
      sessionStorage.setItem('ut_cache_mt_records', JSON.stringify(inMemoryMTRecords));
    } catch (e) {}
    return sorted;
  } catch (error) {
    console.error("Error fetching mandatory training records: ", error);
    if (inMemoryMTRecords) return inMemoryMTRecords.data;
    return [];
  }
};

/**
 * Fetch records specifically for a participant's NIK.
 * This is CRITICAL for saving Firebase quota:
 * Instead of reading the entire collection of all users (1,000+ reads),
 * it only queries documents belonging to that single participant (max 7 reads),
 * reducing reads by over 98% for 150 daily users!
 */
export const getMandatoryRecordsByNik = async (nik: string): Promise<MandatoryTrainingRecord[]> => {
  if (!nik || !nik.trim()) return [];
  const cleanNik = nik.trim();

  // If in-memory records already loaded, filter without any network request
  if (inMemoryMTRecords) {
    const matched = inMemoryMTRecords.data.filter(r => r.nik && r.nik.trim().toLowerCase() === cleanNik.toLowerCase());
    if (matched.length > 0) return matched;
  }

  // Check sessionStorage cache for this NIK
  const cacheKey = `ut_cache_mt_nik_${cleanNik.toLowerCase()}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < TARGETED_QUERY_CACHE_TTL_MS) {
        return parsed.data;
      }
    }
  } catch (e) {}

  try {
    const q = query(
      collection(db, MANDATORY_TRAINING_COLLECTION),
      where("nik", "==", cleanNik)
    );
    const snap = await getDocs(q);
    const records: MandatoryTrainingRecord[] = [];
    snap.forEach((doc) => {
      const data = doc.data() as MandatoryTrainingRecord;
      records.push({
        ...data,
        id: data.id || doc.id
      });
    });

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ data: records, timestamp: Date.now() }));
    } catch (e) {}

    return records;
  } catch (error) {
    console.error("Error fetching mandatory records by NIK: ", error);
    return [];
  }
};

export interface WorkerSuggestion {
  fullName: string;
  nik: string;
  position: string;
  company: string;
  workArea: string;
}

// Combined worker registry cache (from both mandatory_training_records and induction_records)
interface WorkerRegistryEntry {
  fullName: string;
  nik: string;
  position: string;
  company: string;
  workArea: string;
  timestamp: number;
}
let inMemoryWorkerRegistry: { data: WorkerRegistryEntry[]; timestamp: number } | null = null;
const WORKER_REGISTRY_CACHE_KEY = 'ut_worker_registry_v1';
const WORKER_REGISTRY_TTL_MS = 5 * 60 * 1000;

async function getWorkerRegistry(forceRefresh = false): Promise<WorkerRegistryEntry[]> {
  if (!forceRefresh && inMemoryWorkerRegistry && (Date.now() - inMemoryWorkerRegistry.timestamp < WORKER_REGISTRY_TTL_MS)) {
    return inMemoryWorkerRegistry.data;
  }

  if (!forceRefresh) {
    try {
      const stored = sessionStorage.getItem(WORKER_REGISTRY_CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Date.now() - parsed.timestamp < WORKER_REGISTRY_TTL_MS) {
          inMemoryWorkerRegistry = parsed;
          return parsed.data as WorkerRegistryEntry[];
        }
      }
    } catch (e) {}
  }

  const registryMap = new Map<string, WorkerRegistryEntry>();

  try {
    // Fetch from mandatory_training_records
    const mtSnap = await getDocs(collection(db, MANDATORY_TRAINING_COLLECTION));
    mtSnap.forEach((docSnap) => {
      const r = docSnap.data() as MandatoryTrainingRecord;
      const nik = (r.nik || '').trim().toLowerCase();
      const name = (r.fullName || '').trim().toLowerCase();
      const key = nik || name;
      if (!key) return;
      const existing = registryMap.get(key);
      if (!existing || (r.timestamp || 0) > existing.timestamp) {
        registryMap.set(key, {
          fullName: r.fullName || '',
          nik: r.nik || '',
          position: r.position || '',
          company: r.company || '',
          workArea: r.workArea || '',
          timestamp: r.timestamp || 0
        });
      }
    });
  } catch (e) {
    console.error("Error fetching mandatory records for worker registry:", e);
  }

  try {
    // Fetch from induction_records (safety induction participants)
    const indSnap = await getDocs(collection(db, RECORDS_COLLECTION));
    indSnap.forEach((docSnap) => {
      const r = docSnap.data() as InductionRecord;
      const name = (r.fullName || '').trim().toLowerCase();
      const key = name;
      if (!key) return;
      // Induction records don't have nik; use fullName as key
      const existing = registryMap.get(key);
      const candidate: WorkerRegistryEntry = {
        fullName: r.fullName || '',
        nik: '',
        position: r.jobTitle || '',
        company: r.company || '',
        workArea: r.area || '',
        timestamp: r.timestamp || 0
      };
      if (!existing || candidate.timestamp > existing.timestamp) {
        // Only merge if no nik from mandatory records
        if (existing && existing.nik) {
          // Keep the nik from mandatory, but update position/company if empty
          if (!existing.position && candidate.position) existing.position = candidate.position;
          if (!existing.company && candidate.company) existing.company = candidate.company;
          if (!existing.workArea && candidate.workArea) existing.workArea = candidate.workArea;
        } else {
          registryMap.set(key, candidate);
        }
      }
    });
  } catch (e) {
    console.error("Error fetching induction records for worker registry:", e);
  }

  const registry = Array.from(registryMap.values());
  inMemoryWorkerRegistry = { data: registry, timestamp: Date.now() };
  try {
    sessionStorage.setItem(WORKER_REGISTRY_CACHE_KEY, JSON.stringify(inMemoryWorkerRegistry));
  } catch (e) {}

  return registry;
}

export const searchMandatoryWorkersByName = async (searchTerm: string): Promise<WorkerSuggestion[]> => {
  if (!searchTerm || searchTerm.trim().length < 2) return [];
  const raw = searchTerm.trim().toLowerCase();

  const cacheKey = `ut_worker_name_${raw}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < TARGETED_QUERY_CACHE_TTL_MS) {
        return parsed.data as WorkerSuggestion[];
      }
    }
  } catch (e) {}

  try {
    const registry = await getWorkerRegistry();
    const matched = registry
      .filter(w => w.fullName && w.fullName.trim().toLowerCase().includes(raw))
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 8)
      .map(w => ({
        fullName: w.fullName,
        nik: w.nik,
        position: w.position,
        company: w.company,
        workArea: w.workArea
      }));

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ data: matched, timestamp: Date.now() }));
    } catch (e) {}
    return matched;
  } catch (error) {
    console.error("Error searching workers by name:", error);
    return [];
  }
};

export const searchMandatoryWorkersByNik = async (searchTerm: string): Promise<WorkerSuggestion[]> => {
  if (!searchTerm || searchTerm.trim().length < 2) return [];
  const raw = searchTerm.trim().toLowerCase();

  const cacheKey = `ut_worker_nik_${raw}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < TARGETED_QUERY_CACHE_TTL_MS) {
        return parsed.data as WorkerSuggestion[];
      }
    }
  } catch (e) {}

  try {
    const registry = await getWorkerRegistry();
    const matched = registry
      .filter(w => w.nik && w.nik.trim().toLowerCase().includes(raw))
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 8)
      .map(w => ({
        fullName: w.fullName,
        nik: w.nik,
        position: w.position,
        company: w.company,
        workArea: w.workArea
      }));

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ data: matched, timestamp: Date.now() }));
    } catch (e) {}
    return matched;
  } catch (error) {
    console.error("Error searching workers by NIK:", error);
    return [];
  }
};

export const subscribeMandatoryTrainingRecords = (callback: (records: MandatoryTrainingRecord[]) => void): (() => void) => {
  return onSnapshot(collection(db, MANDATORY_TRAINING_COLLECTION), (querySnapshot) => {
    const records: MandatoryTrainingRecord[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as MandatoryTrainingRecord;
      records.push({
        ...data,
        id: data.id || doc.id
      });
    });
    const sorted = records.sort((a, b) => b.timestamp - a.timestamp);
    inMemoryMTRecords = { data: sorted, timestamp: Date.now() };
    try {
      sessionStorage.setItem('ut_cache_mt_records', JSON.stringify(inMemoryMTRecords));
    } catch (e) {}
    callback(sorted);
  }, (error) => {
    console.error("Error subscribing to mandatory training records: ", error);
    if (inMemoryMTRecords) {
      callback(inMemoryMTRecords.data);
    } else {
      callback([]);
    }
  });
};

export const saveMandatoryTrainingRecord = async (record: MandatoryTrainingRecord): Promise<void> => {
  try {
    const payload = removeUndefinedFields(record);
    await setDoc(doc(db, MANDATORY_TRAINING_COLLECTION, record.id), payload);
    
    // Update local cache
    if (inMemoryMTRecords) {
      inMemoryMTRecords.data = [record, ...inMemoryMTRecords.data.filter(r => r.id !== record.id)];
      inMemoryMTRecords.timestamp = Date.now();
      try {
        sessionStorage.setItem('ut_cache_mt_records', JSON.stringify(inMemoryMTRecords));
      } catch (e) {}
    }

    // Update NIK cache
    if (record.nik) {
      const cacheKey = `ut_cache_mt_nik_${record.nik.trim().toLowerCase()}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        let existing: MandatoryTrainingRecord[] = [];
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed.data)) {
            existing = parsed.data;
          }
        }
        const updated = [record, ...existing.filter(r => r.id !== record.id)];
        sessionStorage.setItem(cacheKey, JSON.stringify({ data: updated, timestamp: Date.now() }));
      } catch (e) {}
    }
    // Invalidate worker registry cache so new workers appear in future searches
    inMemoryWorkerRegistry = null;
    try {
      sessionStorage.removeItem(WORKER_REGISTRY_CACHE_KEY);
    } catch (e) {}
  } catch (error) {
    console.error("Error saving mandatory training record: ", error);
    throw error;
  }
};

export const deleteMandatoryTrainingRecord = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, MANDATORY_TRAINING_COLLECTION, id));
    if (inMemoryMTRecords) {
      inMemoryMTRecords.data = inMemoryMTRecords.data.filter(r => r.id !== id);
      try {
        sessionStorage.setItem('ut_cache_mt_records', JSON.stringify(inMemoryMTRecords));
      } catch (e) {}
    }
  } catch (error) {
    console.error("Error deleting mandatory training record: ", error);
    throw error;
  }
};

export const getMandatorySettings = async (forceRefresh = false): Promise<MandatorySettings> => {
  if (!forceRefresh) {
    const cached = getFromCacheWithTtl<MandatorySettings>('ut_mandatory_settings_cache_v5', CONFIG_CACHE_TTL_MS);
    if (cached) {
      return cached;
    }
  }

  try {
    const docRef = doc(db, SETTINGS_COLLECTION, MANDATORY_SETTINGS_DOC);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const settingsData = data.settings || data;
      const defaultQuestions = getDefaultModuleQuestions();

      // Merge and ensure each of the 7 modules has its complete questions in Firestore
      const customQuestionsMap: Record<string, PostTestQuestion[]> = {
        ...defaultQuestions,
        ...(settingsData.customQuestions || {})
      };

      let needsFirestoreSync = false;
      MANDATORY_MODULES.forEach(mod => {
        if (!customQuestionsMap[mod.id] || customQuestionsMap[mod.id].length < 10) {
          customQuestionsMap[mod.id] = [...mod.questions];
          needsFirestoreSync = true;
        }
      });

      const settings: MandatorySettings = {
        ...DEFAULT_MANDATORY_SETTINGS,
        ...settingsData,
        allowedWorkAreas: (settingsData.allowedWorkAreas && settingsData.allowedWorkAreas.length > 0)
          ? settingsData.allowedWorkAreas
          : DEFAULT_MANDATORY_SETTINGS.allowedWorkAreas,
        activeModules: {
          ...DEFAULT_MANDATORY_SETTINGS.activeModules,
          ...(settingsData.activeModules || {})
        },
        modulePdfs: {
          ...DEFAULT_MANDATORY_SETTINGS.modulePdfs,
          ...(settingsData.modulePdfs || {})
        },
        customQuestions: customQuestionsMap
      };

      // Normalize any dummy /modules/*.pdf references to the real official Google Drive PDF URL
      if (settings.modulePdfs) {
        for (const k in settings.modulePdfs) {
          if (!settings.modulePdfs[k] || settings.modulePdfs[k].startsWith('/modules/')) {
            settings.modulePdfs[k] = SAFETY_INDUCTION_PDF_URL;
          }
        }
      }

      // Persist full populated questions to Firebase Firestore if needed
      if (needsFirestoreSync) {
        setDoc(docRef, removeUndefinedFields({ settings, ...settings, updatedAt: Date.now() }), { merge: true }).catch(() => {});
        // Also persist each module to mandatory_questions collection
        MANDATORY_MODULES.forEach(mod => {
          const modDocRef = doc(db, MANDATORY_QUESTIONS_COLLECTION, mod.id);
          setDoc(modDocRef, removeUndefinedFields({
            moduleId: mod.id,
            moduleTitle: mod.title,
            badge: mod.badge,
            questions: customQuestionsMap[mod.id],
            updatedAt: Date.now()
          }), { merge: true }).catch(() => {});
        });
      }

      setToCacheWithTtl('ut_mandatory_settings_cache_v5', settings);
      return settings;
    } else {
      const initialSettings = {
        ...DEFAULT_MANDATORY_SETTINGS,
        customQuestions: getDefaultModuleQuestions(),
        updatedAt: Date.now()
      };
      await setDoc(docRef, removeUndefinedFields({ settings: initialSettings, ...initialSettings }));
      
      // Seed dedicated mandatory_questions collection in Firestore
      for (const mod of MANDATORY_MODULES) {
        const modDocRef = doc(db, MANDATORY_QUESTIONS_COLLECTION, mod.id);
        await setDoc(modDocRef, removeUndefinedFields({
          moduleId: mod.id,
          moduleTitle: mod.title,
          badge: mod.badge,
          questions: mod.questions,
          updatedAt: Date.now()
        }), { merge: true });
      }

      setToCacheWithTtl('ut_mandatory_settings_cache_v5', initialSettings);
      return initialSettings;
    }
  } catch (error) {
    console.error("Error fetching mandatory settings: ", error);
    const cached = getFromCacheWithTtl<MandatorySettings>('ut_mandatory_settings_cache_v5', Infinity);
    return cached || DEFAULT_MANDATORY_SETTINGS;
  }
};

export const subscribeMandatorySettings = (callback: (settings: MandatorySettings) => void): (() => void) => {
  const docRef = doc(db, SETTINGS_COLLECTION, MANDATORY_SETTINGS_DOC);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      const settingsData = data.settings || data;
      const settings: MandatorySettings = {
        ...DEFAULT_MANDATORY_SETTINGS,
        ...settingsData,
        allowedWorkAreas: (settingsData.allowedWorkAreas && settingsData.allowedWorkAreas.length > 0)
          ? settingsData.allowedWorkAreas
          : DEFAULT_MANDATORY_SETTINGS.allowedWorkAreas,
        activeModules: {
          ...DEFAULT_MANDATORY_SETTINGS.activeModules,
          ...(settingsData.activeModules || {})
        },
        modulePdfs: {
          ...DEFAULT_MANDATORY_SETTINGS.modulePdfs,
          ...(settingsData.modulePdfs || {})
        },
        customQuestions: {
          ...DEFAULT_MANDATORY_SETTINGS.customQuestions,
          ...(settingsData.customQuestions || {})
        }
      };

      if (settings.modulePdfs) {
        for (const k in settings.modulePdfs) {
          if (!settings.modulePdfs[k] || settings.modulePdfs[k].startsWith('/modules/')) {
            settings.modulePdfs[k] = SAFETY_INDUCTION_PDF_URL;
          }
        }
      }

      if (settings.customQuestions) {
        for (const k in settings.customQuestions) {
          const qList = settings.customQuestions[k];
          if (!Array.isArray(qList) || qList.length < 10) {
            delete settings.customQuestions[k];
          }
        }
      }

      setToCacheWithTtl('ut_mandatory_settings_cache_v5', settings);
      callback(settings);
    } else {
      setDoc(docRef, removeUndefinedFields({ settings: DEFAULT_MANDATORY_SETTINGS, ...DEFAULT_MANDATORY_SETTINGS })).then(() => {
        setToCacheWithTtl('ut_mandatory_settings_cache_v5', DEFAULT_MANDATORY_SETTINGS);
        callback(DEFAULT_MANDATORY_SETTINGS);
      }).catch(err => {
        console.error("Error initializing mandatory settings in subscriber: ", err);
        callback(DEFAULT_MANDATORY_SETTINGS);
      });
    }
  }, (error) => {
    console.error("Error subscribing to mandatory settings: ", error);
    const cached = getFromCacheWithTtl<MandatorySettings>('ut_mandatory_settings_cache_v5', Infinity);
    callback(cached || DEFAULT_MANDATORY_SETTINGS);
  });
};

export const saveMandatorySettings = async (settings: MandatorySettings): Promise<void> => {
  try {
    const docRef = doc(db, SETTINGS_COLLECTION, MANDATORY_SETTINGS_DOC);
    const cleanedSettings = removeUndefinedFields({ ...settings, updatedAt: Date.now() });
    const payload = removeUndefinedFields({
      ...cleanedSettings,
      settings: cleanedSettings
    });
    await setDoc(docRef, payload);
    setToCacheWithTtl('ut_mandatory_settings_cache_v5', settings);

    // Also persist each module's questions to dedicated mandatory_questions collection
    if (settings.customQuestions) {
      for (const [modId, qList] of Object.entries(settings.customQuestions)) {
        if (Array.isArray(qList)) {
          const modInfo = MANDATORY_MODULES.find(m => m.id === modId);
          setDoc(doc(db, MANDATORY_QUESTIONS_COLLECTION, modId), removeUndefinedFields({
            moduleId: modId,
            moduleTitle: modInfo?.title || modId,
            badge: modInfo?.badge,
            questions: qList,
            updatedAt: Date.now()
          }), { merge: true }).catch(() => {});
        }
      }
    }
  } catch (error) {
    console.error("Error saving mandatory settings to Firebase: ", error);
    throw error;
  }
};

/**
 * Saves or updates questions for a specific mandatory module in Firebase
 */
export const saveMandatoryModuleQuestions = async (moduleId: string, questions: PostTestQuestion[]): Promise<void> => {
  try {
    const mod = MANDATORY_MODULES.find(m => m.id === moduleId);
    
    // 1. Save to dedicated mandatory_questions collection
    await setDoc(doc(db, MANDATORY_QUESTIONS_COLLECTION, moduleId), removeUndefinedFields({
      moduleId,
      moduleTitle: mod?.title || moduleId,
      badge: mod?.badge,
      questions,
      updatedAt: Date.now()
    }), { merge: true });

    // 2. Save to mandatory_settings_config
    const current = await getMandatorySettings();
    const updatedCustom = {
      ...(current.customQuestions || {}),
      [moduleId]: questions
    };
    await saveMandatorySettings({
      ...current,
      customQuestions: updatedCustom
    });
  } catch (error) {
    console.error(`Error saving questions for module ${moduleId} to Firebase:`, error);
    throw error;
  }
};

/**
 * Synchronizes ALL questions across the entire platform directly to Firebase Firestore:
 * 1. 11 Attendance/General Induction Questions -> app_settings/questions_list
 * 2. 10 Induction Post-Test Questions -> app_settings/post_test_questions_list
 * 3. All 70 Mandatory Module Questions (10 per module x 7 modules) ->
 *    a. app_settings/mandatory_settings_config.customQuestions
 *    b. mandatory_questions/{moduleId} dedicated collection
 */
export const syncAllQuestionsToFirebase = async (): Promise<{
  success: boolean;
  totalInductionQuestions: number;
  totalPostTestQuestions: number;
  totalMandatoryQuestions: number;
  totalAllQuestions: number;
}> => {
  try {
    // 1. Sync Induction Attendance Questions
    const questionsPayload = removeUndefinedFields({ questions: INITIAL_QUESTIONS, updatedAt: Date.now() });
    await setDoc(doc(db, SETTINGS_COLLECTION, QUESTIONS_DOC), questionsPayload, { merge: true });
    setToCacheWithTtl('ut_questions_cache_v5', INITIAL_QUESTIONS);

    // 2. Sync Induction Post-Test Questions
    const postTestPayload = removeUndefinedFields({ questions: DEFAULT_POST_TEST_QUESTIONS, updatedAt: Date.now() });
    await setDoc(doc(db, SETTINGS_COLLECTION, POST_TEST_QUESTIONS_DOC), postTestPayload, { merge: true });
    setToCacheWithTtl('ut_pt_questions_cache_v5', DEFAULT_POST_TEST_QUESTIONS);

    // 3. Sync All 7 Mandatory Module Questions
    const defaultModuleQuestions = getDefaultModuleQuestions();
    
    const mandatoryDocRef = doc(db, SETTINGS_COLLECTION, MANDATORY_SETTINGS_DOC);
    const existingSnap = await getDoc(mandatoryDocRef);
    const existingData = existingSnap.exists() ? (existingSnap.data().settings || existingSnap.data()) : {};

    const updatedCustomQuestions = {
      ...defaultModuleQuestions,
      ...(existingData.customQuestions || {})
    };

    // Guarantee each module has at least 10 questions
    MANDATORY_MODULES.forEach(mod => {
      if (!updatedCustomQuestions[mod.id] || updatedCustomQuestions[mod.id].length < 10) {
        updatedCustomQuestions[mod.id] = [...mod.questions];
      }
    });

    const fullSettingsPayload = removeUndefinedFields({
      settings: {
        ...DEFAULT_MANDATORY_SETTINGS,
        ...existingData,
        customQuestions: updatedCustomQuestions,
        updatedAt: Date.now()
      },
      ...DEFAULT_MANDATORY_SETTINGS,
      ...existingData,
      customQuestions: updatedCustomQuestions,
      updatedAt: Date.now()
    });
    await setDoc(mandatoryDocRef, fullSettingsPayload, { merge: true });

    // 4. Save to dedicated mandatory_questions collection
    for (const mod of MANDATORY_MODULES) {
      const qList = updatedCustomQuestions[mod.id] || mod.questions;
      const modDocRef = doc(db, MANDATORY_QUESTIONS_COLLECTION, mod.id);
      await setDoc(modDocRef, removeUndefinedFields({
        moduleId: mod.id,
        moduleTitle: mod.title,
        badge: mod.badge,
        questionCount: qList.length,
        questions: qList,
        updatedAt: Date.now()
      }), { merge: true });
    }

    let mandatoryQuestionsCount = 0;
    Object.values(updatedCustomQuestions).forEach(qs => {
      mandatoryQuestionsCount += qs.length;
    });

    setToCacheWithTtl('ut_mandatory_settings_cache_v5', {
      ...DEFAULT_MANDATORY_SETTINGS,
      ...existingData,
      customQuestions: updatedCustomQuestions
    });

    return {
      success: true,
      totalInductionQuestions: INITIAL_QUESTIONS.length,
      totalPostTestQuestions: DEFAULT_POST_TEST_QUESTIONS.length,
      totalMandatoryQuestions: mandatoryQuestionsCount,
      totalAllQuestions: INITIAL_QUESTIONS.length + DEFAULT_POST_TEST_QUESTIONS.length + mandatoryQuestionsCount
    };
  } catch (err) {
    console.error("Error syncing all questions to Firebase:", err);
    throw err;
  }
};

/**
 * Clear client-side cache helper (useful if admin wants a full reset)
 */
export const clearStorageCache = (): void => {
  inMemoryInductionRecords = null;
  inMemoryMTRecords = null;
  inMemoryAdmins = null;
  try {
    sessionStorage.removeItem('ut_cache_induction_records');
    sessionStorage.removeItem('ut_cache_mt_records');
    localStorage.removeItem('ut_questions_cache_v3');
    localStorage.removeItem('ut_pt_questions_cache_v3');
    localStorage.removeItem('ut_instructors_cache_v3');
    localStorage.removeItem('ut_mandatory_settings_cache_v3');
    localStorage.removeItem('ut_questions_cache_v4');
    localStorage.removeItem('ut_pt_questions_cache_v4');
    localStorage.removeItem('ut_instructors_cache_v4');
    localStorage.removeItem('ut_mandatory_settings_cache_v4');
  } catch (e) {}
};
